import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const fmt = (n) =>
  n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : `$${(n || 0).toFixed(2)}`;

const TICK_STYLE = { fontFamily: "IBM Plex Mono", fontSize: 11, fill: "#8a8a8a" };
const TOOLTIP_STYLE = {
  background: "#050505",
  border: "1px solid #222",
  borderRadius: 0,
  fontFamily: "IBM Plex Mono",
  fontSize: 12,
};
const Y_DOMAIN = ["dataMin", "dataMax"];
const yTickFormatter = (v) => (Number(v) || 0).toFixed(6);

export default function CoinChart({ coin, price, chart }) {
  const up = (price?.change_24h || 0) >= 0;
  return (
    <div className="border border-[#3a3a3a] bg-[#121212] mb-3" data-testid="coin-chart-card">
      {!coin.lp_pool_id && (
        <div className="flex items-center gap-2 px-6 py-2 border-b border-[#3a3a3a] bg-[#161616] text-[13px] text-[#e2e2e2]" data-testid="paper-price-notice">
          <span className="font-mono text-[10px] uppercase tracking-widest bg-[#ffd700] text-[#050505] px-1.5 py-0.5">Paper price</span>
          Simulated for the demo trader — not a market price. Real pricing appears once a Raydium pool is live.
        </div>
      )}
      <div className="flex items-baseline gap-4 flex-wrap px-6 py-4 border-b border-[#3a3a3a]">
        <div className="font-display font-bold text-lg">${coin.ticker} / USD</div>
        <div className="font-mono text-2xl font-bold">
          ${typeof price?.price === "number" ? price.price.toFixed(8) : "—"}
        </div>
        <div className={`flex items-center gap-1 font-mono text-xs ${up ? "text-[#00ff41]" : "text-[#ff3333]"}`}>
          {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {price?.change_24h?.toFixed(2) || "0.00"}% 24h
        </div>
        <div className="ml-auto flex gap-6 font-mono text-[11px] uppercase tracking-widest">
          <div>
            <div className="text-[#d0d0d0]">M.Cap</div>
            <div className="text-white">{fmt(price?.market_cap)}</div>
          </div>
          <div>
            <div className="text-[#d0d0d0]">Vol 24h</div>
            <div className="text-white">{fmt(price?.volume_24h)}</div>
          </div>
        </div>
      </div>
      <div className="h-72 px-2 pt-4 pb-2">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={{ width: 320, height: 240 }}>
          <AreaChart data={chart}>
            <defs>
              <linearGradient id="coin-grad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#cfff04" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#cfff04" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1a1a1a" vertical={false} />
            <XAxis dataKey="t" stroke="#333" tick={TICK_STYLE} interval="preserveStartEnd" minTickGap={56} />
            <YAxis
              stroke="#333"
              tick={TICK_STYLE}
              tickFormatter={yTickFormatter}
              domain={Y_DOMAIN}
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [`$${(Number(v) || 0).toFixed(10)}`, coin.ticker]}
            />
            <Area type="monotone" dataKey="p" stroke="#cfff04" strokeWidth={1.5} fill="url(#coin-grad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
